import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { DatasetListItem, DatasetListResponse } from "datasets/types/interfaces/datasetListItem";
import { PanelRightOpen, UserRoundPlus } from "lucide-react";
import useSWR from "swr";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import useNextGenHeader from "~/contexts/nextgen-header";
import { usePageContext } from "~/contexts/page-context";
import useUser from "~/contexts/user";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFetcher } from "~/hooks/useFetcher";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import { buildEntityBreadcrumbs } from "~/pages/manifold-nextgen/breadcrumbs";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import {
  NEXTGEN_CHAT_EVENT,
  trackNextgenChatEvent,
} from "~/pages/manifold-nextgen/components/conversations/analytics";
import { McpAppsPaneProvider } from "~/pages/manifold-nextgen/components/conversations/useMcpAppsPane";
import { SessionDagRunsProvider } from "~/pages/manifold-nextgen/components/conversations/useSessionDagRuns";
import { SessionEvalRunsProvider } from "~/pages/manifold-nextgen/components/conversations/useSessionEvalRuns";
import { SessionIngestsProvider } from "~/pages/manifold-nextgen/components/conversations/useSessionIngests";
import { SessionPlanProvider } from "~/pages/manifold-nextgen/components/conversations/useSessionPlan";
import { ProjectShareRecipientWarning } from "~/pages/manifold-nextgen/components/projects/ProjectShareRecipientWarning";
import { OwnersOnlyDialog } from "~/pages/manifold-nextgen/components/share/OwnersOnlyDialog";
import { ShareModal } from "~/pages/manifold-nextgen/components/share/ShareModal";
import { useProjectNeedsSetup } from "~/pages/manifold-nextgen/hooks/useProjectNeedsSetup";
import { useProjectShare } from "~/pages/manifold-nextgen/hooks/useProjectShare";
import { SessionContextPanelProvider } from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";
import {
  nextgenProjectPath,
  nextgenProjectsListPath,
  withProjectId,
} from "~/pages/manifold-nextgen/paths";
import { Agent, agentPath } from "~/services/agents";
import { catalogDatasetsListUrl } from "~/services/catalog";
import {
  FeaturedSession,
  Project,
  ProjectAccessLevel,
  ProjectConversation,
  projectAgentsPath,
  projectConversationsPath,
  projectDatasetsPath,
  projectFeaturedSessionsPath,
  projectPath,
  projectRouteId,
} from "~/services/projects";
import { apiClient } from "~/utils/apiClient";
import { ProjectSidebar } from "./ProjectSidebar";
import { SessionContextSidebar } from "./SessionContextSidebar";

export interface ProjectOutletContext {
  projectId: string;
  project: Project;
  // Whether the caller owns (can_manage) this project. Gates owner-only controls.
  isOwner: boolean;
  sessions: ProjectConversation[] | undefined;
  featuredSessions: FeaturedSession[] | undefined;
  attachedAgents: Agent[] | undefined;
  attachedAgentsWithDetails: Agent[] | undefined;
  attachedDatasets: DatasetListItem[] | undefined;
  // Un-collapses the session context sidebar — for entry points outside the
  // sidebar itself (e.g. the New Session splash's Overview link) that need it
  // visible before opening a panel in it.
  expandContextSidebar: () => void;
  // Set when any fetch that gates the project contents (agents, datasets, or
  // the catalog they resolve against) fails. ProjectIndex reads this to show an
  // error instead of hanging on its loading skeleton.
  attachedEntitiesError: unknown;
}

export const ProjectLayout = () => {
  // The route param is `:nextgenProjectId` so the embedded workbench subtree can
  // own `:projectId` — see the projects route in `routes.tsx`.
  const { nextgenProjectId: projectId } = useParams<{ nextgenProjectId: string }>();
  const location = useLocation();
  const currentUser = useUser();
  // A project opened/viewed — fires on mount and on switch, the one choke point
  // every entry path (home, projects list, cards, deep link) passes through.
  useEffect(() => {
    if (!projectId) return;
    trackNextgenChatEvent(NEXTGEN_CHAT_EVENT.PROJECT_OPENED, { projectId });
  }, [projectId]);
  const [projectShareOpen, setProjectShareOpen] = useState(false);
  const [preferences, updatePreferences] = useUserPreferences();
  const [isContextSidebarCollapsed, setIsContextSidebarCollapsed] = useState(
    preferences?.collapseSessionContextSidebar ?? false
  );

  const handleCollapseContextSidebar = (collapsed: boolean) => {
    setIsContextSidebarCollapsed(collapsed);
    updatePreferences({ collapseSessionContextSidebar: collapsed });
  };

  const {
    collaborators,
    isLoading: collaboratorsLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel,
  } = useProjectShare(projectId ?? "");

  const {
    data: project,
    error,
    isLoading,
  } = useFetcher<Project>(projectId ? projectPath(projectId) : null);

  const { data: attachedAgents, error: attachedAgentsError } = useFetcher<Agent[]>(
    projectId ? projectAgentsPath(projectId) : null
  );

  const agentIds = attachedAgents?.map((a) => a.id);
  // Fetches full agent details (including starter_prompts) individually because
  // GET /projects/:id/agents returns a lightweight shape without that field.
  // Ideally starter_prompts would be included in that endpoint to avoid N requests.
  const { data: agentsWithDetails } = useSWR<Agent[]>(
    agentIds?.length ? ["project-agents-full", ...[...agentIds].sort()] : null,
    ([, ...ids]: [string, ...string[]]) =>
      Promise.all(ids.map((id) => apiClient<Agent>(agentPath(id))))
  );

  const { data: attachedDatasetIds, error: attachedDatasetsError } = useFetcher<string[]>(
    projectId ? projectDatasetsPath(projectId) : null
  );

  const { data: catalogDatasetList, error: catalogError } =
    useFetcher<DatasetListResponse>(catalogDatasetsListUrl);
  const catalogDatasets = catalogDatasetList?.items;

  // Any of these failing leaves attachedAgents/attachedDatasets undefined, so
  // surface the first error to ProjectIndex rather than hanging on its skeleton.
  const attachedEntitiesError = attachedAgentsError ?? attachedDatasetsError ?? catalogError;

  const { data: sessionsList } = useFetcher<{ items: ProjectConversation[] }>(
    projectId ? projectConversationsPath(projectId) : null
  );
  const sessions = sessionsList?.items;

  const { data: featuredSessions } = useFetcher<FeaturedSession[]>(
    projectId ? projectFeaturedSessionsPath(projectId) : null
  );

  const attachedDatasets = useMemo<DatasetListItem[] | undefined>(() => {
    if (attachedDatasetIds === undefined || catalogDatasets === undefined) {
      return undefined;
    }
    const byId = new Map(catalogDatasets.map((d) => [d.id, d]));
    return attachedDatasetIds
      .map((id) => byId.get(id))
      .filter((d): d is DatasetListItem => Boolean(d));
  }, [attachedDatasetIds, catalogDatasets]);

  const conversationId = location.pathname.match(/\/c\/([^/]+)/)?.[1];
  // The featured-session route carries a share token instead of a
  // conversation id — the rail has no notion of the latter — so its name has
  // to be looked up in `featuredSessions` rather than `sessions`.
  const featuredToken = location.pathname.match(/\/featured\/([^/]+)/)?.[1];
  const isOwner = project?.access_level === ProjectAccessLevel.OWNER;

  // The chats route renders ProjectSetupWizard instead of the new-session
  // picker until the project has at least one enabled agent (for an owner —
  // mirrors ProjectIndex's own "setup" mode condition). Datasets are optional.
  const needsSetup = useProjectNeedsSetup(attachedAgents, isOwner);

  // The session context sidebar only shows on the new-session picker and
  // individual session pages — not on Overview, Settings, the setup wizard,
  // or the read-only featured-session view, which use the full content area
  // for their own purposes.
  const isChatsRoute = /\/c$/.test(location.pathname);
  const showContextSidebar = (isChatsRoute && !needsSetup) || !!conversationId;
  const currentSession = conversationId
    ? sessions?.find((s) => s.id === conversationId)
    : undefined;
  const currentFeaturedSession = featuredToken
    ? featuredSessions?.find((f) => f.share_token === featuredToken)
    : undefined;

  const sectionLabel = currentSession?.display_name ?? currentFeaturedSession?.display_name;
  const showSectionCrumb = Boolean(conversationId || featuredToken);

  useDocumentTitle(sectionLabel ?? project?.name ?? "");

  // Always show "All → Project Name". When inside a session (editable or a
  // featured one viewed read-only), add its name as a trailing crumb so
  // users know which conversation they're in.
  const breadcrumbs = useMemo(
    () =>
      buildEntityBreadcrumbs({
        rootHref: nextgenProjectsListPath(),
        entityName: project?.name ?? "Loading…",
        entityHref: showSectionCrumb && projectId ? nextgenProjectPath(projectId) : undefined,
        sectionLabel: showSectionCrumb ? (sectionLabel ?? "Loading…") : undefined,
      }),
    [project?.name, projectId, showSectionCrumb, sectionLabel]
  );

  const handleOpenProjectShare = useCallback(() => setProjectShareOpen(true), []);

  // Only surface header actions once the project has loaded: the modals they
  // open are rendered in the success-path return below, so controls shown
  // during load or on the error state would click into nothing.
  const headerActions = useMemo<[React.ReactNode?]>(() => {
    if (!project) {
      return [];
    }
    // Share slot represents the project (the entity the header is scoped to).
    // Owners get the full share flow; everyone else sees the same button, but
    // it opens a read-only list of owners to contact instead (below). Session
    // sharing lives inline in the composer. Project-level settings (leave /
    // delete) live on the Settings page reached from the sidebar.
    const shareNode = (
      <Button key="share-project" size="sm" className="text-sm" onClick={handleOpenProjectShare}>
        <UserRoundPlus className="size-4" />
        Share
      </Button>
    );

    return [shareNode];
  }, [project, handleOpenProjectShare]);

  useNextGenHeader({ breadcrumbs, actions: headerActions });
  usePageContext(
    project ? { page: "project", name: project.name, id: projectRouteId(project) } : null
  );

  if (!projectId) {
    return (
      <div role="alert" className="p-6 text-sm text-destructive">
        Missing project id.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full overflow-hidden">
        <div
          className="w-56 shrink-0 border-r border-border"
          role="status"
          aria-label="Loading project"
        />
        <div className="flex-1" />
      </div>
    );
  }

  if (error || !project) {
    return <ResourceLoadError error={error} resourceLabel="project" className="h-full" />;
  }

  // A project resolves from either identifier, but only its short_id addresses
  // it on the analysis-agent's create-session route, and everything below reads
  // the id straight off the URL. Home links a recent session by the project's
  // `resource_id` — its uuid — so canonicalize before rendering the outlet.
  // `short_id` is nullable until its backfill is tightened; while it is absent
  // the uuid URL is the one that works, so leave it be.
  if (project.short_id && projectId !== project.short_id) {
    return (
      <Navigate
        replace
        to={`${withProjectId(location.pathname, project.short_id)}${location.search}${location.hash}`}
      />
    );
  }

  const outletContext: ProjectOutletContext = {
    projectId,
    project,
    isOwner,
    sessions,
    featuredSessions,
    attachedAgents,
    attachedAgentsWithDetails: agentsWithDetails,
    attachedDatasets,
    attachedEntitiesError,
    expandContextSidebar: () => handleCollapseContextSidebar(false),
  };

  return (
    <>
      <div className="flex h-full overflow-hidden">
        <ProjectSidebar
          projectId={projectId}
          workbenchId={project.workbench_id}
          sessions={sessions}
          isOwner={isOwner}
          featuredSessions={featuredSessions}
          attachedAgentsWithDetails={agentsWithDetails}
        />
        <SessionContextPanelProvider conversationId={conversationId}>
          <McpAppsPaneProvider>
            <SessionIngestsProvider>
              <SessionEvalRunsProvider>
                <SessionDagRunsProvider>
                  <SessionPlanProvider conversationId={conversationId}>
                    <div className="relative flex flex-1 flex-col overflow-hidden">
                      <Outlet context={outletContext} />
                      {showContextSidebar && isContextSidebarCollapsed && (
                        <Tooltip delayDuration={700}>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleCollapseContextSidebar(false)}
                              aria-label="Expand session context"
                              className="absolute right-2 top-2 z-10"
                            >
                              <PanelRightOpen className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">Toggle Context</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    {showContextSidebar && (
                      <SessionContextSidebar
                        projectId={projectId}
                        isOwner={outletContext.isOwner}
                        attachedDatasets={attachedDatasets}
                        attachedAgents={attachedAgents}
                        isCollapsed={isContextSidebarCollapsed}
                        onCollapse={() => handleCollapseContextSidebar(true)}
                      />
                    )}
                  </SessionPlanProvider>
                </SessionDagRunsProvider>
              </SessionEvalRunsProvider>
            </SessionIngestsProvider>
          </McpAppsPaneProvider>
        </SessionContextPanelProvider>
      </div>
      {isOwner ? (
        <ShareModal
          open={projectShareOpen}
          onOpenChange={setProjectShareOpen}
          resourceLabel="project"
          resourceName={project.name}
          collaborators={collaborators}
          collaboratorsLoading={collaboratorsLoading}
          collaboratorsError={collaboratorsError}
          accessLevels={accessLevels}
          defaultAccessLevel={defaultAccessLevel}
          onShare={handleShare}
          currentUserEmail={currentUser?.email}
          allowGeneralAccess
          renderRecipientWarning={(recipient) => (
            <ProjectShareRecipientWarning
              projectId={project.id}
              recipient={recipient}
              agents={attachedAgents}
              datasets={attachedDatasets}
            />
          )}
        />
      ) : (
        <OwnersOnlyDialog
          open={projectShareOpen}
          onOpenChange={setProjectShareOpen}
          resourceLabel="project"
          resourceName={project.name}
          ownerLevel={ProjectAccessLevel.OWNER}
          collaborators={collaborators}
          collaboratorsLoading={collaboratorsLoading}
          collaboratorsError={collaboratorsError}
        />
      )}
    </>
  );
};
